import { IState, IValue } from "./state";
import { ActionTree, Store, ActionContext } from 'vuex';
import * as api from '../api';
import { ISearchParams } from "../api";
import { router } from '../main';

type PayloadType = 'initApp' | 'setState' | 'setSearchQuery' | 'search' | 'toggleValue' | 'setCurrentPage' | 'clearFilters' | 'setStartDate' | 'setEndDate' | 'setSort'
export type ActionPayload<ActionData> = {
	type: PayloadType,
	payload: ActionData
}

type ActionHandler<ActionData> = (this: Store<IState>, injectee: ActionContext<IState, IState>, payload: ActionPayload<ActionData>) => any;
type ActionCreator<ActionData> = (data: ActionData) => ActionPayload<ActionData>;

export interface IValuePayload {
	name: string,
	value: string,
	selected: boolean
}

export interface ISetSearchQueryPayload {
	query: string,
	skipSearch?: boolean
}

export interface IDatePayload {
	start: string,
	end: string
}

export interface IToggleValuePayload {
	value: IValuePayload,
	skipSearch?: boolean
}

export interface IClearFiltersPayload {
	skipSearch?: boolean
}

export interface ISetCurrentPagePayload {
	page: number,
	skipSearch?: boolean
}


// ACTION CREATORS

export const initApp: ActionCreator<IState> = (state) => ({
	type: 'initApp',
	payload: state
})

export const setSearchQuery: ActionCreator<ISetSearchQueryPayload> = (queryPayload) => ({
	type: 'setSearchQuery',
	payload: queryPayload
})

export const search: ActionCreator<boolean> = (reset) => ({
	type: 'search',
	payload: reset
})

export const setStartDate: ActionCreator<string> = (date) => ({
	type: 'setStartDate',
	payload: date
})

export const setEndDate: ActionCreator<string> = (date) => ({
	type: 'setEndDate',
	payload: date
})

// Toggle values in filter, payload: name (of filter), value, selected
export const toggleValue: ActionCreator<IToggleValuePayload> = (value) => ({
	type: 'toggleValue',
	payload: value
})

export const setCurrentPage: ActionCreator<ISetCurrentPagePayload> = (page) => ({
	type: 'setCurrentPage',
	payload: page
})

export const clearFilters: ActionCreator<IClearFiltersPayload> = (payload) => ({
	type: 'clearFilters',
	payload
})

export const setFiltersFromParams: ActionCreator<string> = (filtersString) => ({
	type: 'clearFilters',
	payload: filtersString
})

export const setSort: ActionCreator<string> = (string) => ({
	type: 'setSort',
	payload: string
})



// ACTION HANDLERS

// Clear all filters and do a new search
const clearFiltersAction: ActionHandler<IClearFiltersPayload> = ({ commit, dispatch, getters }, { payload }) => {
	commit({
		type: 'clearFilters',
		payload
	})
	router.replace({
		query: getters.getParams
	})
	if(!payload.skipSearch) {
		dispatch(search(true))
	}
}

// Make a new search with the selected value in filter
const toggleValueAction: ActionHandler<IToggleValuePayload> = ({ commit, dispatch }, { payload }) => {
	commit({
		type: 'toggleValue',
		payload: payload.value
	})
	if(!payload.skipSearch) {
		dispatch(search(true))
	}
}

// Do the search
const searchAction: ActionHandler<boolean> = ({ commit, state }, { payload }) => {

	if(payload) {
		commit({
			type: 'resetPage',
			payload: undefined
		})
	}

	// Get the identifier from filter and its selected values, use in searchParams
	const categoryFilters = state.categoryFilter.map(cf => {
		return {
			identifier: cf.identifier,
			values: cf.values.filter(v => v.selected).map(v => v.value)
		}
	})

	// Find active sort
	const sort = state.sort.find(s => s.active);

	// searchParams get the query from state, categoryFilters from above, 
	const searchParams = {
		query: state.query,
		categories: categoryFilters,
		date: {
			start: state.date.start,
			end: state.date.end
		},
		page: state.page,
		sort: sort
	} as ISearchParams

	// Endpoint = url to search route
	// Send the news hits in the result and the total amount to mutaions
	api.search(state.endpoints.search, searchParams)
		.then(result => {
			commit({
				type: 'setNews',
				payload: result.news
			})
			commit({
				type: 'setTotal',
				payload: result.total
			})
			commit({
				type: 'setPages',
				payload: Math.ceil(Number(state.total) / Number(state.count))
			})
		});
}

const initAppAction: ActionHandler<IState> = ({ commit, state }, { payload }) => {
	commit({
		type: 'setState',
		payload
	})
}

// Make a new search with the search query
const setSearchQueryAction: ActionHandler<ISetSearchQueryPayload> = ({ commit, dispatch }, { payload }) => {
	commit({
		type: 'setSearchQuery',
		payload: payload.query
	})
	if(!payload.skipSearch) {
		dispatch(search(true))
	}
}

// Set the start date filter and do a new search
const setStartDateAction: ActionHandler<IDatePayload> = ({ commit, dispatch }, { payload }) => {
	commit({
		type: 'setStartDate',
		payload
	})
	dispatch(search(true))
}

// Set the end date filter and do a new search
const setEndDateAction: ActionHandler<IDatePayload> = ({ commit, dispatch }, { payload }) => {
	commit({
		type: 'setEndDate',
		payload
	})
	dispatch(search(true))
}

// Set the current page for the pagination
const setCurrentPageAction: ActionHandler<ISetCurrentPagePayload> = ({ commit, dispatch, state }, { payload }) => {
	commit({
		type: 'setCurrentPage',
		payload: payload.page
	})
	if(!payload.skipSearch) {
		dispatch(search(false))
	}
}

// Set the end date filter and do a new search
const setSortAction: ActionHandler<string> = ({ commit, dispatch }, { payload }) => {
	commit({
		type: 'setSort',
		payload
	})
	dispatch(search(true))
}

const actions: ActionTree<IState, IState> = {
	initApp: initAppAction,
	setSearchQuery: setSearchQueryAction,
	search: searchAction,
	toggleValue: toggleValueAction,
	setCurrentPage: setCurrentPageAction,
	clearFilters: clearFiltersAction,
	setStartDate: setStartDateAction,
	setEndDate: setEndDateAction,
	setSort: setSortAction
}

export default actions;
